/* je-instock-brand-fit-20260827 (+ je-brand-stage-span-20260827)
   Keeps the vertical maker signature beside each cutout's actual visible
   pixels horizontally, while its word now spans from the top of the art stage
   into the pedestal so the plinth naturally masks the lower end. */
(function () {
  'use strict';

  if (window.__jeInStockBrandFitWired) return;

  // je-ghost-poster-cards-20260830: the ATTRIBUTE marks a poster grid, not the
  // class. /pages/euronics-partner renders its poster cards into
  // `.je-ghost__grid`, so a `.collection-grid`-scoped lookup found nothing and
  // the fitter returned before doing any work.
  var grid = document.querySelector('[data-je-in-stock-cards]');
  if (!grid) return;
  window.__jeInStockBrandFitWired = true;

  // Same selector as the stylesheet's, so the fitter and the paint cannot
  // disagree about which cards are poster cards. A ghost card is a <div>.
  var CARD_SELECTOR = ':is(a, div).product-card:not(.product-card--plain)';
  var GAP_PX = 6;
  /* je-brand-top-at-product-20260827: the shortest strip the word may be asked
     to fit into, and the smallest type it may be set in. Both are needed and
     they pull opposite ways: the span floor is what stops a soundbar 6% of the
     card tall demanding a 6px word, and the font floor is what stops a long
     name ("Russell Hobbs" is 105px of vertical type at 12px) being SHEARED by
     the block's own `overflow: hidden` on a 170px phone card. The span floor
     is a proportion of the stage, not a fixed 74px, because 74px is a quarter
     of a desktop card and nearly half a phone one. */
  var MIN_BRAND_SPAN_RATIO = 0.22;
  var MIN_BRAND_SPAN_FLOOR_PX = 52;
  var MIN_FONT_PX = 9;
  var EDGE_PX = 4;
  var MAX_GROWTH = 2.6;
  var PEDESTAL_OVERLAP = 0.37;
  var ALPHA_THRESHOLD = 12;
  var MAX_SCAN_EDGE = 384;
  var MAX_CARDS_PER_FRAME = 4;
  var HOVER_SCALE = 1.035;
  var alphaCache = Object.create(null);
  var observedStages = [];
  var queuedCards = [];
  var framePending = false;
  var stats = {
    ready: 0,
    alphaScans: 0,
    corsFallbacks: 0,
    frameFallbacks: 0,
    failures: 0
  };

  function number(value) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
  }

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function layoutOffsetWithin(element, ancestor) {
    var left = 0;
    var top = 0;
    var node = element;

    while (node && node !== ancestor) {
      left += node.offsetLeft || 0;
      top += node.offsetTop || 0;
      if (node.parentNode && node.parentNode.nodeType === 1) {
        left -= node.parentNode.scrollLeft || 0;
        top -= node.parentNode.scrollTop || 0;
      }
      node = node.offsetParent;
    }

    return { left: left, top: top };
  }

  function positionTokenOffset(token, freeSpace, leadingKeyword, trailingKeyword) {
    token = String(token || '50%').toLowerCase();
    if (token === 'center') return freeSpace * 0.5;
    if (token === leadingKeyword) return 0;
    if (token === trailingKeyword) return freeSpace;
    if (token.indexOf('%') !== -1) return freeSpace * number(token) / 100;
    if (token.indexOf('px') !== -1) return number(token);
    return freeSpace * 0.5;
  }

  function objectPosition(style, freeX, freeY) {
    var parts = String(style.objectPosition || '50% 50%').trim().split(/\s+/);
    var x = parts[0] || '50%';
    var y = parts[1] || '50%';

    if (parts.length === 1 && (x === 'top' || x === 'bottom')) {
      y = x;
      x = '50%';
    }

    return {
      left: positionTokenOffset(x, freeX, 'left', 'right'),
      top: positionTokenOffset(y, freeY, 'top', 'bottom')
    };
  }

  function scanAlpha(source, width, height) {
    var scanScale = Math.min(1, MAX_SCAN_EDGE / Math.max(width, height));
    var scanWidth = Math.max(1, Math.round(width * scanScale));
    var scanHeight = Math.max(1, Math.round(height * scanScale));
    var canvas = document.createElement('canvas');
    canvas.width = scanWidth;
    canvas.height = scanHeight;
    var context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('2D canvas unavailable');
    context.drawImage(source, 0, 0, scanWidth, scanHeight);

    var pixels = context.getImageData(0, 0, scanWidth, scanHeight).data;
    var minX = scanWidth;
    var minY = scanHeight;
    var maxX = -1;
    var maxY = -1;

    for (var pixel = 0, alpha = 3; alpha < pixels.length; pixel += 1, alpha += 4) {
      if (pixels[alpha] <= ALPHA_THRESHOLD) continue;
      var x = pixel % scanWidth;
      var y = Math.floor(pixel / scanWidth);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

    if (maxX < minX || maxY < minY) {
      throw new Error('No visible pixels in cutout');
    }

    stats.alphaScans += 1;
    return {
      left: minX * width / scanWidth,
      top: minY * height / scanHeight,
      right: (maxX + 1) * width / scanWidth,
      bottom: (maxY + 1) * height / scanHeight,
      source: 'alpha'
    };
  }

  function fullFrame(width, height) {
    stats.frameFallbacks += 1;
    return null;
  }

  function useFixedFallback(card) {
    var stage = card && card.querySelector('.card-img');
    if (!stage) return;
    stage.classList.remove('je-brand-fit-ready', 'je-brand-fit-motion');
    [
      '--je-brand-fit-left',
      '--je-brand-fit-top',
      '--je-brand-fit-height',
      '--je-brand-fit-font-size',
      '--je-brand-fit-hover-left',
      '--je-brand-fit-hover-top',
      '--je-brand-fit-hover-height'
    ].forEach(function (property) { stage.style.removeProperty(property); });
    stage.setAttribute('data-je-brand-fit-source', 'fixed-fallback');
  }

  function alphaBounds(image) {
    var width = image.naturalWidth;
    var height = image.naturalHeight;
    var key = (image.currentSrc || image.src || '') + '|' + width + 'x' + height;
    if (alphaCache[key]) return alphaCache[key];

    var promise;
    try {
      promise = Promise.resolve(scanAlpha(image, width, height));
    } catch (directError) {
      stats.corsFallbacks += 1;
      promise = new Promise(function (resolve) {
        var cleanImage = new Image();
        cleanImage.crossOrigin = 'anonymous';
        cleanImage.onload = function () {
          try {
            resolve(scanAlpha(cleanImage, width, height));
          } catch (corsError) {
            resolve(fullFrame(width, height));
          }
        };
        cleanImage.onerror = function () {
          resolve(fullFrame(width, height));
        };
        cleanImage.src = image.currentSrc || image.src;
      });
    }

    alphaCache[key] = promise;
    return promise;
  }

  function renderedAlphaBox(stage, image, bounds) {
    var style = getComputedStyle(image);
    var borderLeft = number(style.borderLeftWidth);
    var borderRight = number(style.borderRightWidth);
    var borderTop = number(style.borderTopWidth);
    var borderBottom = number(style.borderBottomWidth);
    var paddingLeft = number(style.paddingLeft);
    var paddingRight = number(style.paddingRight);
    var paddingTop = number(style.paddingTop);
    var paddingBottom = number(style.paddingBottom);
    var contentWidth = image.offsetWidth - borderLeft - borderRight - paddingLeft - paddingRight;
    var contentHeight = image.offsetHeight - borderTop - borderBottom - paddingTop - paddingBottom;
    if (contentWidth <= 0 || contentHeight <= 0) return null;

    var naturalWidth = image.naturalWidth;
    var naturalHeight = image.naturalHeight;
    var scaleX = contentWidth / naturalWidth;
    var scaleY = contentHeight / naturalHeight;
    var fit = style.objectFit || 'fill';

    if (fit === 'contain' || fit === 'scale-down') {
      var containScale = Math.min(scaleX, scaleY);
      if (fit === 'scale-down') containScale = Math.min(1, containScale);
      scaleX = containScale;
      scaleY = containScale;
    } else if (fit === 'cover') {
      var coverScale = Math.max(scaleX, scaleY);
      scaleX = coverScale;
      scaleY = coverScale;
    } else if (fit === 'none') {
      scaleX = 1;
      scaleY = 1;
    }

    var renderedWidth = naturalWidth * scaleX;
    var renderedHeight = naturalHeight * scaleY;
    var position = objectPosition(style, contentWidth - renderedWidth, contentHeight - renderedHeight);
    var offset = layoutOffsetWithin(image, stage);
    var imageLeft = offset.left + borderLeft + paddingLeft + position.left;
    var imageTop = offset.top + borderTop + paddingTop + position.top;
    var originParts = String(style.transformOrigin || '50% 50%').trim().split(/\s+/);
    var originX = positionTokenOffset(originParts[0], image.offsetWidth, 'left', 'right');
    var originY = positionTokenOffset(originParts[1] || '50%', image.offsetHeight, 'top', 'bottom');

    return {
      left: imageLeft + bounds.left * scaleX,
      top: imageTop + bounds.top * scaleY,
      right: imageLeft + bounds.right * scaleX,
      bottom: imageTop + bounds.bottom * scaleY,
      source: bounds.source,
      elementLeft: offset.left,
      elementTop: offset.top,
      originX: originX,
      originY: originY
    };
  }

  function scaledProductBox(box, scale) {
    var anchorX = box.elementLeft + box.originX;
    var anchorY = box.elementTop + box.originY;
    return {
      left: anchorX + (box.left - anchorX) * scale,
      top: anchorY + (box.top - anchorY) * scale,
      right: anchorX + (box.right - anchorX) * scale,
      bottom: anchorY + (box.bottom - anchorY) * scale
    };
  }

  function stageSpan(stage) {
    var stageHeight = stage.clientHeight;
    var pedestal = stage.querySelector('.card-art-pedestal');
    var bottom = stageHeight * 0.86;
    var pedestalTop = stageHeight * 0.805;
    var pedestalHeight = stageHeight * 0.15;

    if (pedestal && pedestal.offsetHeight) {
      pedestalTop = layoutOffsetWithin(pedestal, stage).top;
      pedestalHeight = pedestal.offsetHeight;
      bottom = pedestalTop + pedestalHeight * PEDESTAL_OVERLAP;
    }

    bottom = clamp(bottom, EDGE_PX + 1, stageHeight - EDGE_PX);
    return {
      top: EDGE_PX,
      bottom: bottom,
      height: bottom - EDGE_PX,
      pedestalTop: pedestalTop,
      overlap: Math.max(0, bottom - pedestalTop)
    };
  }

  /* je-brandfit-idempotent-20260830: the natural height of a given string, at a given size,
     in a given style is deterministic -- and independent of the stage, because
     the probe below is `width:max-content`.  A 264-product grid repeats its
     brands heavily, so this is mostly hits.
     ⚠️ CLEARED ON document.fonts.ready.  A height measured before the webfont
     lands is a different number, and a stale hit would size the word wrongly
     for the rest of the session. */
  var jeBrandHeightCache = Object.create(null);
  /* A webfont arriving after a fit would change the measurement, so the epoch
     invalidates every cached signature and re-queues the watched cards.
     ⚠️ This is a BELT, not the braces: measured on the live homepage,
     fonts.ready resolves at ~341ms and the fits happen at ~1113ms, so it does
     not fire in the normal case.  The thing that actually protects the
     geometry is the `je-brand-fit-motion` gate in applyFit -- see the note
     there.  Kept for the cold-cache case where the font genuinely lands late. */
  var jeFitEpoch = 0;
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      jeBrandHeightCache = Object.create(null);
      jeFitEpoch += 1;
      for (var i = 0; i < observedStages.length; i += 1) {
        var c = observedStages[i] && observedStages[i].closest &&
                observedStages[i].closest('a.product-card');
        if (c) queueCard(c);
      }
    });
  }
  function naturalBrandHeight(stage, brand, fontSize) {
    var jeKey = (brand.textContent || '') + '|' + fontSize + '|' + brand.className;
    if (jeBrandHeightCache[jeKey] !== undefined) return jeBrandHeightCache[jeKey];
    var probe = brand.cloneNode(true);
    probe.removeAttribute('id');
    probe.style.setProperty('position', 'absolute', 'important');
    probe.style.setProperty('visibility', 'hidden', 'important');
    probe.style.setProperty('pointer-events', 'none', 'important');
    probe.style.setProperty('left', '-10000px', 'important');
    probe.style.setProperty('right', 'auto', 'important');
    probe.style.setProperty('top', '0', 'important');
    probe.style.setProperty('bottom', 'auto', 'important');
    probe.style.setProperty('width', 'max-content', 'important');
    probe.style.setProperty('height', 'auto', 'important');
    probe.style.setProperty('max-height', 'none', 'important');
    probe.style.setProperty('overflow', 'visible', 'important');
    probe.style.setProperty('display', 'block', 'important');
    probe.style.setProperty('font-size', fontSize + 'px', 'important');
    probe.style.setProperty('transform', 'none', 'important');
    stage.appendChild(probe);
    var height = probe.getBoundingClientRect().height;
    probe.remove();
    jeBrandHeightCache[jeKey] = height;
    return height;
  }

  function applyFit(card, bounds) {
    var stage = card.querySelector('.card-img');
    var image = card.querySelector('img.primary[data-je-card-cutout]');
    var brand = card.querySelector('.card-art-brand');
    if (!stage || !image || !brand || !stage.offsetWidth || !stage.offsetHeight) return;

    /* je-brandfit-idempotent-20260830: the stage's box and the alpha bounds are the ONLY
       inputs to everything below, so if neither has moved the result is
       necessarily the one already on the element -- and doing it again costs
       three forced synchronous layouts (the cloned probe, the brand width read
       and the computed padding read, the last two deliberately after a style
       write and therefore not batchable).
       A re-fit is the common case, not the rare one: a ResizeObserver watches
       every stage, and applyFilters(), "load more", the ghost-catalogue merge
       and page 2 arriving all re-queue cards that have not moved.  Measured,
       applyFit + naturalBrandHeight were 2,200ms of a throttled In Stock load. */
    /* Every runtime input, and only free reads.  renderedAlphaBox() also reads
       the image's own box and its computed padding/border/object-position, but
       those are static CSS: the only way they change is a breakpoint, and a
       breakpoint moves stage.offsetWidth, which is already here.  currentSrc
       and the natural size cover a responsive srcset swapping the file. */
    var jeSig = jeFitEpoch + '|' + stage.offsetWidth + 'x' + stage.offsetHeight + '|' +
                image.offsetWidth + 'x' + image.offsetHeight + '|' +
                (image.currentSrc || image.src || '') + '|' +
                image.naturalWidth + 'x' + image.naturalHeight + '|' +
                (bounds ? [bounds.left, bounds.top, bounds.right, bounds.bottom,
                           bounds.width, bounds.height].join(',') : 'none');
    /* ⛔ THE SECOND FIT IS THE AUTHORITATIVE ONE, AND IT IS NOT ABOUT FONTS.
       Instrumented on the live page: fonts.ready resolves at 341ms and the two
       fits happen at ~1113ms and ~1230ms, so both are post-font.  What moves
       between them is this function's OWN first pass -- it adds
       `je-brand-fit-ready` and writes --je-brand-fit-font-size, and the width
       read immediately afterwards is still settling: measured 48.917 -> 49.317,
       75.621 -> 76.073, 95.516 -> 96.039 on five cards across both viewports.
       The live build keeps the LAST fit; caching the first froze a word 0.45px
       from where it belongs.
       So the signature is only honoured once `je-brand-fit-motion` is present.
       That class is added two rAFs after `je-brand-fit-ready`, i.e. it marks
       "this card's entrance has settled" -- fit 1 runs and caches nothing,
       fit 2 runs and caches, and the repeats that actually cost (resize,
       filters, load-more, the ghost merge) are the ones skipped. */
    var jeSettled = stage.classList.contains('je-brand-fit-motion');
    if (jeSettled && stage.__jeFitSig === jeSig && stage.classList.contains('je-brand-fit-ready')) return;
    if (jeSettled) stage.__jeFitSig = jeSig;

    var box = renderedAlphaBox(stage, image, bounds);
    if (!box) return;

    var stageHeight = stage.clientHeight;
    var span = stageSpan(stage);
    /* Keep the word beside the measured alpha edge. If a full-height short
       brand is wider than that strip, its right side is deliberately allowed
       to pass behind the higher-z product rather than shrinking vertically. */
    var labelRight = box.left - GAP_PX;
    var productTop = clamp(box.top, EDGE_PX, stageHeight - EDGE_PX - 1);
    var productBottom = clamp(box.bottom, productTop + 1, stageHeight - EDGE_PX);

    /* je-brand-top-at-product-20260827 (Will: "the band name only goes as high
       as the product"). The span used to start EDGE_PX below the top of the
       stage whatever the product did, so a wide, short cut-out — a television,
       a soundbar — carried a maker word towering over empty black above it.
       It now starts at the product's own measured alpha top. The BOTTOM is
       untouched: running into the plinth is the deliberate look. */
    var minSpan = Math.max(MIN_BRAND_SPAN_FLOOR_PX, stageHeight * MIN_BRAND_SPAN_RATIO);
    span.top = clamp(Math.max(span.top, productTop), EDGE_PX,
                     Math.max(EDGE_PX, span.bottom - minSpan));
    span.height = span.bottom - span.top;

    /* Remove the fitted rule synchronously before reading the responsive class
       size. Otherwise a ResizeObserver pass would mistake its previous fitted
       size for the class's real baseline and progressively shrink the word. */
    stage.classList.remove('je-brand-fit-ready');
    stage.style.removeProperty('--je-brand-fit-font-size');
    var baseFontSize = number(getComputedStyle(brand).fontSize);
    var requiredHeight = naturalBrandHeight(stage, brand, baseFontSize);
    var availableHeight = Math.max(1, span.height - 4);

    /* Fit every brand length to the same designed vertical span. Growth stays
       bounded so corrupt geometry cannot produce runaway type. */
    var fittedFontSize = baseFontSize;
    if (requiredHeight > 0) {
      fittedFontSize = clamp(
        baseFontSize * availableHeight / requiredHeight * 0.98,
        /* je-brand-top-at-product-20260827: was a flat 12. Once the word starts
           at the product's own top instead of the top of the card, a long name
           over a short product needs to be set smaller than 12px to fit, and
           the block clips rather than overflowing - seven phone cards lost the
           end of "Russell Hobbs" at 12. These are 7.5%-opacity outlined ghost
           letters behind the product, so small is fine and sheared is not. */
        MIN_FONT_PX,
        baseFontSize * MAX_GROWTH
      );
    }

    stage.classList.add('je-brand-fit-ready');
    stage.style.setProperty('--je-brand-fit-font-size', fittedFontSize.toFixed(2) + 'px');

    var brandWidth = brand.getBoundingClientRect().width;
    stage.style.setProperty('--je-brand-fit-top', span.top.toFixed(2) + 'px');
    stage.style.setProperty('--je-brand-fit-height', span.height.toFixed(2) + 'px');

    /* The 0.08em block padding je-instock-poster-v2.css adds is what stops
       `overflow: hidden` shearing the gold outline off the letters. It is dead
       space, so the gap is measured from the INK edge and not from the box, or
       every label would sit further from its product than intended -- and by a
       DIFFERENT distance on each card, since the padding scales with the
       fitted font. Read after the font-size var is set, for that reason. */
    var inkPad = number(getComputedStyle(brand).paddingRight);
    var left = Math.max(EDGE_PX, labelRight - brandWidth + inkPad);
    var hoverBox = scaledProductBox(box, HOVER_SCALE);
    var hoverLeft = Math.max(EDGE_PX, hoverBox.left - GAP_PX - brandWidth + inkPad);
    stage.style.setProperty('--je-brand-fit-left', left.toFixed(2) + 'px');
    stage.style.setProperty('--je-brand-fit-hover-left', hoverLeft.toFixed(2) + 'px');
    /* je-brand-top-at-product-20260827: hover scales the product about
       50%/62%, so its top RISES; the word tracks it rather than staying put. */
    var hoverTop = clamp(Math.max(EDGE_PX, hoverBox.top), EDGE_PX,
                         Math.max(EDGE_PX, span.bottom - Math.max(
                           MIN_BRAND_SPAN_FLOOR_PX, stageHeight * MIN_BRAND_SPAN_RATIO)));
    stage.style.setProperty('--je-brand-fit-hover-top', hoverTop.toFixed(2) + 'px');
    stage.style.setProperty('--je-brand-fit-hover-height',
                            (span.bottom - hoverTop).toFixed(2) + 'px');
    stage.setAttribute('data-je-brand-fit-source', box.source);
    stage.setAttribute('data-je-brand-fit-gap', GAP_PX);
    stage.setAttribute('data-je-brand-fit-product-left', box.left.toFixed(2));
    stage.setAttribute('data-je-brand-fit-product-top', box.top.toFixed(2));
    stage.setAttribute('data-je-brand-fit-product-bottom', box.bottom.toFixed(2));
    stage.setAttribute('data-je-brand-fit-hover-product-left', hoverBox.left.toFixed(2));
    stage.setAttribute('data-je-brand-fit-hover-product-top', hoverBox.top.toFixed(2));
    stage.setAttribute('data-je-brand-fit-hover-product-bottom', hoverBox.bottom.toFixed(2));
    stage.setAttribute('data-je-brand-fit-base-font', baseFontSize.toFixed(2));
    stage.setAttribute('data-je-brand-fit-floor-top', span.top.toFixed(2));
    stage.setAttribute('data-je-brand-span-top', span.top.toFixed(2));
    stage.setAttribute('data-je-brand-span-bottom', span.bottom.toFixed(2));
    stage.setAttribute('data-je-brand-span-pedestal-top', span.pedestalTop.toFixed(2));
    stage.setAttribute('data-je-brand-span-overlap', span.overlap.toFixed(2));
    stage.setAttribute('data-je-brand-fit-ink-pad', inkPad.toFixed(2));
    stage.setAttribute('data-je-brand-fit-required-height', requiredHeight.toFixed(2));
    if (!stage.classList.contains('je-brand-fit-motion') && !stage.__jeBrandFitMotionQueued) {
      stage.__jeBrandFitMotionQueued = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          stage.__jeBrandFitMotionQueued = false;
          if (stage.classList.contains('je-brand-fit-ready')) stage.classList.add('je-brand-fit-motion');
        });
      });
    }
    stats.ready += 1;
  }

  function fitCard(card) {
    var image = card && card.querySelector('img.primary[data-je-card-cutout]');
    if (!image) return;
    if (!card.offsetWidth || !card.offsetHeight) return;

    if (!image.complete || !image.naturalWidth || !image.naturalHeight) {
      if (!image.__jeBrandFitLoadWired) {
        image.__jeBrandFitLoadWired = true;
        image.addEventListener('load', function () { queueCard(card); }, { once: true });
        image.addEventListener('error', function () { stats.failures += 1; }, { once: true });
      }
      return;
    }

    alphaBounds(image).then(function (bounds) {
      if (!document.documentElement.contains(card)) return;
      if (bounds) applyFit(card, bounds);
      else useFixedFallback(card);
    }).catch(function () {
      stats.failures += 1;
      useFixedFallback(card);
    });
  }

  function flushQueue() {
    framePending = false;
    var cards = queuedCards.splice(0, MAX_CARDS_PER_FRAME);
    for (var i = 0; i < cards.length; i += 1) fitCard(cards[i]);
    if (queuedCards.length) {
      framePending = true;
      requestAnimationFrame(flushQueue);
    }
  }

  function queueCard(card) {
    if (!card || !card.offsetWidth || !card.offsetHeight || queuedCards.indexOf(card) !== -1) return;
    queuedCards.push(card);
    if (!framePending) {
      framePending = true;
      requestAnimationFrame(flushQueue);
    }
  }

  function watchCard(card) {
    if (!card || card.__jeBrandFitObserved) return;
    card.__jeBrandFitObserved = true;
    var stage = card.querySelector('.card-img');
    if (!stage) return;
    observedStages.push(stage);
    if (resizeObserver) resizeObserver.observe(stage);
    queueCard(card);
  }

  var resizeObserver = window.ResizeObserver ? new ResizeObserver(function (entries) {
    for (var i = 0; i < entries.length; i += 1) {
      queueCard(entries[i].target.closest(CARD_SELECTOR));
    }
  }) : null;

  var mutationObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i += 1) {
      var added = mutations[i].addedNodes;
      for (var n = 0; n < added.length; n += 1) {
        var node = added[n];
        if (!node || node.nodeType !== 1) continue;
        if (node.matches && node.matches(CARD_SELECTOR)) watchCard(node);
        if (node.querySelectorAll) {
          var cards = node.querySelectorAll(CARD_SELECTOR);
          for (var c = 0; c < cards.length; c += 1) watchCard(cards[c]);
        }
      }
    }
  });

  var initialCards = grid.querySelectorAll(CARD_SELECTOR);
  for (var i = 0; i < initialCards.length; i += 1) watchCard(initialCards[i]);
  mutationObserver.observe(grid, { childList: true, subtree: true });

  if (!resizeObserver) {
    var resizeTimer = 0;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var cards = grid.querySelectorAll(CARD_SELECTOR);
        for (var i = 0; i < cards.length; i += 1) queueCard(cards[i]);
      }, 100);
    }, { passive: true });
  }

  window.__jeInStockBrandFit = {
    refit: function () {
      var cards = grid.querySelectorAll(CARD_SELECTOR);
      for (var i = 0; i < cards.length; i += 1) queueCard(cards[i]);
    },
    stats: stats,
    alphaCache: alphaCache
  };
})();
